import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Title } from 'react-head';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Toolbar,
  Typography,
  AppBar,
  ThemeProvider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import { useNavigate } from 'react-router-dom';
import EastIcon from '@mui/icons-material/East';

import useAuth from '../../hooks/useAuth';
import lightThemeConfig from '../../Theme/light';
import utils from '../../utils/Utils';
import { NimbusConfig, VaultConfig } from '../../const/ProductConfigs';

const productConfigs = {
  Nimbus: NimbusConfig,
  Vault: VaultConfig,
};

const Launcher = () => {
  const [productList, setProductList] = useState([]);

  const {
    getProductsAssociatedToTenant,
    getTokenFromAuthCode,
    getCsrfToken,
    checkActiveSession,
  } = useAuth();
  const navigate = useNavigate();
  const parameters = new URLSearchParams(window.location.search);
  const productRedirectURL = decodeURIComponent(parameters.get('redirect'));
  const tenant = window.CONFIG.TENANT || localStorage.getItem('TENANT');
  const getAuthHeaders = useCallback(async () => {
    const deviceId = await utils.getOrCreateDeviceId();
    return {
      'X-Tenant-ID': tenant,
      'X-Device-ID': deviceId,
    };
  }, [tenant]);

  const getCookie = useCallback((name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }, []);

  const handleApplicationClick = useCallback(
    async (item) => {
      const currentTenant =
        window.CONFIG.TENANT || localStorage.getItem('TENANT') || tenant;
      const headers = await getAuthHeaders();
      const isProductRedirectURLValid =
        productRedirectURL && productRedirectURL !== 'null';
      let isLocalhost = false;
      if (isProductRedirectURLValid) {
        const productOrigin = new URL(productRedirectURL)?.origin;
        isLocalhost = productOrigin?.includes('localhost') || false;
      }

      try {
        await getCsrfToken({ 'X-Tenant-ID': currentTenant });
        const sessionResponse = await checkActiveSession(
          {
            'X-Tenant-ID': currentTenant,
            'X-csrftoken': getCookie('csrftoken'),
          },
          { product_id: item?.id }
        );
        const { redirect_url: redirectUrlFromAPI, session_id: sessionId } =
          sessionResponse?.data;
        if (redirectUrlFromAPI) {
          const code = new URL(redirectUrlFromAPI)?.searchParams?.get('code');
          const tokenRes = await getTokenFromAuthCode?.(
            { 'X-Tenant-ID': headers?.['X-Tenant-ID'] },
            {
              code,
              product_id: item?.id,
            }
          );
          const { access_token: AccessToken, refresh_token: RefreshToken } =
            tokenRes?.data ?? {};
          let baseUrl;
          try {
            baseUrl = new URL(
              isProductRedirectURLValid &&
              (() => {
                try {
                  const domainCheck =
                    new URL(productRedirectURL)?.origin ===
                    new URL(redirectUrlFromAPI)?.origin;
                  return domainCheck || isLocalhost;
                } catch {
                  return false;
                }
              })()
                ? productRedirectURL
                : redirectUrlFromAPI
            );
          } catch {
            try {
              baseUrl = new URL(redirectUrlFromAPI);
            } catch {
              return;
            }
          }
          const updated = utils.updateUrlParams(
            baseUrl,
            {
              access_token: AccessToken,
              refresh_token: RefreshToken,
              realm_id: headers?.['X-Tenant-ID'],
              session_id: sessionId,
              device_id: headers?.['X-Device-ID'],
            },
            ['code']
          );
          window.location.href = updated?.toString?.();
        }
      } catch {
        let validProductRedirectURL = null;
        if (isProductRedirectURLValid) {
          try {
            validProductRedirectURL = new URL(productRedirectURL)?.toString?.();
          } catch {
            validProductRedirectURL = null;
          }
        } else {
          validProductRedirectURL = null;
        }
        const baseUrl =
          (validProductRedirectURL &&
            new URL(validProductRedirectURL)?.origin ===
              new URL(item?.redirect_url)?.origin) ||
          isLocalhost
            ? validProductRedirectURL
            : item?.redirect_url;
        if (productConfigs?.[item?.name]) {
          navigate(productConfigs?.[item?.name]?.ROUTE, {
            state: {
              product: item?.name,
              redirect: baseUrl,
              productId: item?.id,
            },
          });
        }
      }
    },
    [
      getAuthHeaders,
      getCsrfToken,
      checkActiveSession,
      getTokenFromAuthCode,
      navigate,
      productRedirectURL,
      tenant,
    ]
  );

  const fetchProductList = useCallback(async (tenantId, callback) => {
    try {
      const response = await getProductsAssociatedToTenant({
        'X-Tenant-ID': tenantId,
      });
      setProductList(response?.data?.data);
      if (
        callback &&
        response?.data?.data &&
        response?.data?.data?.length === 1
      ) {
        localStorage?.setItem?.('TENANT', tenantId);
        setTimeout(() => {
          callback(response?.data?.data[0]);
        }, 50);
      }
    } catch {
      setProductList([]);
    }
  }, []);
  const formik = useFormik({
    initialValues: { tenant: '' },
    validationSchema: Yup.object({
      tenant: Yup.string().required('Required'),
    }),
    onSubmit: (values) => {
      localStorage?.setItem?.('TENANT', values?.tenant);
      setTimeout(() => {
        fetchProductList?.(values?.tenant, handleApplicationClick);
      }, 50);
    },
  });

  const onTenantChange = useCallback(
    (e) => {
      const newVal = e.target.value;
      if (newVal) {
        formik.setFieldValue('tenant', newVal);
      } else {
        formik.setFieldValue('tenant', '');
        setProductList([]);
      }
    },
    [formik.setFieldValue, formik.values.tenant]
  );

  useEffect(() => {
    const product = window.CONFIG.PRODUCT;

    if (tenant && product) {
      localStorage.setItem('TENANT', tenant);
      if (productConfigs[product]) {
        navigate(productConfigs[product].ROUTE, {
          state: {
            product,
            redirect: productRedirectURL,
          },
        });
      }
      return;
    }
    if (tenant) {
      formik.setFieldValue('tenant', tenant);
      fetchProductList(tenant);
    }
  }, []);

  const theme = useMemo(() => lightThemeConfig, []);

  return (
    <>
      <Title>Launcher</Title>
      <ThemeProvider theme={theme}>
        <AppBar position="absolute" elevation={2}>
          <Toolbar>
            <AppsIcon
              style={{
                color: theme.palette.primary.main,
                width: 24,
                height: 24,
              }}
            />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1 }}
              ml={1}
              title="App Launcher"
              aria-label="App Launcher"
            >
              App Launcher
            </Typography>
          </Toolbar>
        </AppBar>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height="100vh"
          sx={{
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Grid item alignItems="center" textAlign="center" mb={4}>
            <Typography
              variant="h1"
              mb={3}
              title="Welcome to the Model Risk Ecosystem"
              aria-label="Welcome to the Model Risk Ecosystem"
            >
              Welcome to the Model Risk Ecosystem
            </Typography>
            <Typography
              variant="subtitle2"
              title="Please enter an organization to continue"
              aria-label="Please enter an organization to continue"
            >
              Please enter an organization to continue*
            </Typography>

            <Box
              component="form"
              noValidate
              onSubmit={formik.handleSubmit}
              width={360}
              mx="auto"
              display="flex"
              flexDirection="row"
              gap={2}
            >
              <TextField
                required
                placeholder="Enter organization name"
                fullWidth
                name="tenant"
                value={formik.values.tenant}
                onChange={onTenantChange}
                onBlur={formik.handleBlur}
                aria-label="tenant"
                helperText={
                  formik.touched.tenant && formik.errors.tenant
                    ? formik.errors.tenant
                    : null
                }
                error={formik.touched.tenant && Boolean(formik.errors.tenant)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" aria-label="submit">
                        <EastIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    paddingRight: 1,
                  },
                }}
              />
            </Box>

            {formik.values.tenant && productList?.length > 0 && (
              <Card
                sx={{
                  width: 'auto',
                  backgroundColor: theme.palette.secondary.main,
                  p: 4,
                  mt: 4,
                  overflow: 'hidden',
                  flexWrap: 'wrap',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-evenly',
                    padding: '0 !important',
                  }}
                >
                  {formik.values.tenant &&
                    productList.map((p) => (
                      <Card
                        key={p.id}
                        onClick={() => handleApplicationClick(p)}
                        sx={{
                          width: 230,
                          height: 202,
                          cursor: 'pointer',
                          margin: 1,
                          transition: 'transform 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.08)',
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 175,
                              height: 128,
                              padding: 1,
                            }}
                          >
                            {(() => {
                              const Icon = productConfigs[p.name]?.ICON;
                              return Icon ? (
                                <Icon width={175} height={128} />
                              ) : null;
                            })()}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            title={p.name}
                            aria-label={p.name}
                            sx={{
                              textAlign: 'center',
                              color: theme.palette?.other?.black,
                            }}
                          >
                            {productConfigs[p.name]
                              ? productConfigs[p.name].DISPLAY_NAME
                              : p.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </ThemeProvider>
    </>
  );
};

export default Launcher;
